declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-MJ8JBRV9YF';
const GADS_CONVERSION_ID = 'AW-17386951240';
const ANALYTICS_CONSENT_KEY = 'egovex_analytics_consent';

export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true';
};

export const setAnalyticsConsent = (granted: boolean): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, granted ? 'true' : 'false');
    if (granted && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted'
      });
    }
  }
};

export const revokeAnalyticsConsent = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(ANALYTICS_CONSENT_KEY);
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      });
    }
  }
};

export const initAnalyticsConsent = (): void => {
  if (typeof window !== 'undefined' && hasAnalyticsConsent() && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted'
    });
  }
};

export const trackPageView = (pagePath: string, pageTitle?: string, isAuthenticated?: boolean) => {
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle || document.title,
      user_type: isAuthenticated ? 'authenticated' : 'anonymous',
    });
  }
};

export const trackEvent = (
  eventName: string,
  params?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    window.gtag('event', eventName, {
      ...params,
    });
  }
};

export const trackLogin = (method: string = 'email') => {
  trackEvent('login', { method });
};

export const trackSignUp = (method: string = 'email') => {
  trackEvent('sign_up', { method });
  
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_CONVERSION_ID}/signup`,
      value: 1.0,
      currency: 'EUR'
    });
  }
};

export const trackLogout = () => {
  trackEvent('logout');
};

export const trackOnboardingStep = (step: number, stepName: string) => {
  trackEvent('onboarding_step', {
    step_number: step,
    step_name: stepName,
  });
};

export const trackOnboardingComplete = () => {
  trackEvent('onboarding_complete');
};

export const trackAskSubmitted = (promptLength: number) => {
  trackEvent('ask_submitted', {
    prompt_length: promptLength,
  });
};

export const trackInsightViewed = (insightType: string) => {
  trackEvent('insight_viewed', {
    insight_type: insightType,
  });
};

export const trackJournalCreated = (hasImage: boolean) => {
  trackEvent('journal_created', {
    has_image: hasImage,
  });
};

export const trackDecisionRecorded = () => {
  trackEvent('decision_recorded');
};

export const trackVoiceStarted = () => {
  trackEvent('voice_started');
};

export const trackVoiceCompleted = (durationSeconds?: number) => {
  trackEvent('voice_completed', {
    duration_seconds: durationSeconds,
  });
};

export const trackFeatureUsed = (featureName: string) => {
  trackEvent('feature_used', {
    feature_name: featureName,
  });
};
